import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../../hooks/axiosInstance";
import { Oval } from "react-loader-spinner";
import { toast } from "react-toastify";
import CreateMessage from "../../../components/Modal/Admin/Messaging/CreateMessage";
import ViewMessage from "../../../components/Modal/Admin/Messaging/ViewMessage";

const STATUS_BADGE_CLASS = {
  pending: "bg-warning",
  sent: "bg-success",
  failed: "bg-danger",
  partial: "bg-info",
};

const RECIPIENT_TYPE_LABELS = {
  base_user: "Base User",
  account: "Account",
  product: "Product",
};

const INITIAL_FILTERS = {
  filter: "",
  recipient_type: "",
  status: "",
  recipient_email: "",
  start_date: "",
  end_date: "",
};

export default function Messaging() {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [showView, setShowView] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState(null);

  const handleShowCreate = () => setShowCreate(true);
  const handleShowView = (id) => {
    setActiveMessageId(id);
    setShowView(true);
  };
  const handleClose = () => {
    setShowCreate(false);
    setShowView(false);
  };
  const handleRefresh = () => setRefreshKey((prev) => prev + 1);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== "");

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/user/messaging/fetch", {
          params: { page, ...filters },
        });
        setMessages(response.data.data.messages.data || []);
        setPagination(response.data.data.messages);
      } catch (error) {
        toast.error("Error fetching messages");
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [page, filters, refreshKey]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.last_page) {
      setPage(newPage);
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0" style={{ color: "#c4281c" }}>Messaging</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/app/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">Messaging</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {loading && !hasActiveFilters ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
          <Oval
            height={80}
            width={80}
            color="#FF0200"
            visible={true}
            ariaLabel="oval-loading"
            secondaryColor="#4fa94d"
            strokeWidth={2}
            strokeWidthSecondary={2}
          />
        </div>
      ) : (
        <div className="row" id="messagingList">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-header d-flex align-items-center border-0">
                <h5 className="card-title mb-0 flex-grow-1">Sent Messages</h5>
                <button
                  className="btn btn-primary"
                  style={{ backgroundColor: "#c4281b", border: "none" }}
                  onClick={handleShowCreate}
                >
                  <i className="mdi mdi-plus-circle-outline me-1"></i>
                  Compose Message
                </button>
              </div>

              <div className="card-body border border-dashed border-end-0 border-start-0">
                <div className="row g-2 align-items-end">
                  {/* Subject search */}
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label small text-muted mb-1">Subject</label>
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Search by subject..."
                        name="filter"
                        value={filters.filter}
                        onChange={handleFilterChange}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </div>

                  {/* Recipient Email */}
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label small text-muted mb-1">Recipient Email</label>
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Search by email..."
                        name="recipient_email"
                        value={filters.recipient_email}
                        onChange={handleFilterChange}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </div>

                  {/* Recipient Type */}
                  <div className="col-lg-2 col-md-4">
                    <label className="form-label small text-muted mb-1">Recipient Type</label>
                    <select
                      className="form-select"
                      name="recipient_type"
                      value={filters.recipient_type}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Types</option>
                      <option value="base_user">Base User</option>
                      <option value="account">Account</option>
                      <option value="product">Product</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div className="col-lg-2 col-md-4">
                    <label className="form-label small text-muted mb-1">Status</label>
                    <select
                      className="form-select"
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="sent">Sent</option>
                      <option value="partial">Partial</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>

                  {/* Clear filters */}
                  <div className="col-lg-2 col-md-4">
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={handleClearFilters}
                      disabled={!hasActiveFilters}
                    >
                      <i className="ri-refresh-line me-1"></i>
                      Clear Filters
                    </button>
                  </div>
                </div>

                <div className="row g-2 align-items-end mt-1">
                  {/* Start Date */}
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label small text-muted mb-1">Start Date</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="ri-calendar-line"></i>
                      </span>
                      <input
                        type="date"
                        className="form-control"
                        name="start_date"
                        value={filters.start_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>

                  {/* End Date */}
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label small text-muted mb-1">End Date</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="ri-calendar-line"></i>
                      </span>
                      <input
                        type="date"
                        className="form-control"
                        name="end_date"
                        value={filters.end_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body">
                {loading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Oval height={40} width={40} color="#FF0200" visible={true} secondaryColor="#4fa94d" />
                  </div>
                ) : (
                  <>
                    <div className="table-responsive table-card">
                      <table
                        className="table align-middle table-nowrap table table-striped table-borderless table-hover"
                        id="messagingTable"
                      >
                        <thead className="table-light text-muted">
                          <tr>
                            <th scope="col">#</th>
                            <th scope="col">Subject</th>
                            <th scope="col">Recipient Type</th>
                            <th scope="col">Scope</th>
                            <th scope="col">Recipients</th>
                            <th scope="col">Status</th>
                            <th scope="col">Sent At</th>
                            <th scope="col"></th>
                          </tr>
                        </thead>
                        <tbody className="list form-check-all">
                          {messages.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="text-center py-4 text-muted">
                                No messages found.
                              </td>
                            </tr>
                          ) : (
                            messages.map((message, index) => (
                              <tr key={message.id}>
                                <td>{(page - 1) * (pagination.per_page || 25) + index + 1}</td>
                                <td>{message.subject}</td>
                                <td>{RECIPIENT_TYPE_LABELS[message.recipient_type] || message.recipient_type}</td>
                                <td className="text-capitalize">{message.recipient_scope}</td>
                                <td>{message.recipient_count}</td>
                                <td>
                                  <span className={`badge ${STATUS_BADGE_CLASS[message.status] || "bg-secondary"}`}>
                                    {message.status}
                                  </span>
                                </td>
                                <td>{new Date(message.created_at).toLocaleString()}</td>
                                <td>
                                  <Link
                                    className="dropdown-item"
                                    onClick={() => handleShowView(message.id)}
                                  >
                                    <i className="ri-eye-fill text-muted me-2 align-bottom"></i>
                                    View
                                  </Link>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
                      <small className="text-muted">
                        {pagination.total
                          ? `Showing ${pagination.from ?? 0}–${pagination.to ?? 0} of ${pagination.total} messages`
                          : ""}
                      </small>
                      <nav>
                        <ul className="pagination mb-0">
                          <li className={`page-item ${pagination.current_page === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pagination.current_page - 1)}
                              disabled={pagination.current_page === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {Array.from({ length: pagination.last_page || 0 }, (_, i) => i + 1).map((n) => (
                            <li
                              key={n}
                              className={`page-item ${n === pagination.current_page ? "active" : ""}`}
                            >
                              <button className="page-link" onClick={() => handlePageChange(n)}>
                                {n}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${pagination.current_page === pagination.last_page ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pagination.current_page + 1)}
                              disabled={pagination.current_page === pagination.last_page}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <CreateMessage show={showCreate} closeModal={handleClose} refreshPage={handleRefresh} />
      <ViewMessage showView={showView} closeModal={handleClose} messageId={activeMessageId} />
    </>
  );
}
